// Dialog de edição do remetente (mailFrom) da configuração do WL
// (§4.3). O fluxo é criar com o padrão (noreply@pay2b.net) e ALTERAR
// depois: RHF + Zod (validação de e-mail) → PATCH …/configuration com
// update otimista + rollback (useUpdateWhiteLabelMailFrom).
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

import { useUpdateWhiteLabelMailFrom } from "../configuration-mutations"

const mailFromPrefix =
  "pages.notificationCenter.configurationsDetailPage.mailFromDialog"

const buildMailFromSchema = (invalidEmail: string) =>
  z.object({
    mailFrom: z.string().email({ message: invalidEmail }),
  })

type WhiteLabelMailFromDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  whiteLabelId: string
  currentMailFrom: string
}

export const WhiteLabelMailFromDialog = ({
  open,
  onOpenChange,
  whiteLabelId,
  currentMailFrom,
}: WhiteLabelMailFromDialogProps) => {
  const { t } = useTranslation()
  const updateMailFrom =
    useUpdateWhiteLabelMailFrom(whiteLabelId)

  const form = useForm({
    resolver: zodResolver(
      buildMailFromSchema(
        t(`${mailFromPrefix}.invalidEmail`)
      )
    ),
    defaultValues: { mailFrom: currentMailFrom },
  })

  const handleSubmit = form.handleSubmit((data) => {
    updateMailFrom.mutate(
      { mailFrom: data.mailFrom },
      { onSuccess: () => onOpenChange(false) }
    )
  })

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      form.reset({ mailFrom: currentMailFrom })
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(`${mailFromPrefix}.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${mailFromPrefix}.description`)}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="mailFrom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${mailFromPrefix}.label`)}
                  </FormLabel>
                  <FormControl>
                    {/* type="text" (não "email"): a validação de
                        e-mail é do Zod; type="email" faria o jsdom/
                        browser bloquear o submit antes do resolver. */}
                    <Input
                      {...field}
                      inputMode="email"
                      placeholder="noreply@pay2b.net"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                {t(`${mailFromPrefix}.cancel`)}
              </Button>
              <Button
                type="submit"
                className="btn-whitelabel"
                disabled={updateMailFrom.isPending}
              >
                {t(`${mailFromPrefix}.save`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
